import { useState, useCallback, useMemo } from "react";
import { Calendar, Users, Trophy } from "lucide-react";
import useRailFilters from "../../../../components/dashboards/mobile/hooks/useRailFilters";

/**
 * NCAAF-specific rail filters hook modeled after NFL rail filters.
 * Preserves existing NCAAF filter options: date, conference, team, top25/division tabs.
 */
const useNCAAFRailFilters = ({
  onFiltersChange,
  initialDate,
  initialConference = null,
  initialTeam = null
}) => {
  const [activeTab, setActiveTab] = useState("all");
  const [showDateSheet, setShowDateSheet] = useState(false);
  const [showConferenceSheet, setShowConferenceSheet] = useState(false);
  const [showTeamSheet, setShowTeamSheet] = useState(false);

  const defaultFilters = useMemo(() => {
    if (typeof initialDate === "string") {
      return {
        date: initialDate,
        conference: initialConference,
        team: initialTeam,
        division: "all"
      };
    }

    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSaturday = dayOfWeek === 6 ? 0 : dayOfWeek === 0 ? 6 : 6 - dayOfWeek;
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysUntilSaturday);
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, "0");
    const day = String(targetDate.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    return {
      date: dateStr,
      conference: initialConference,
      team: initialTeam,
      division: "all"
    };
  }, [initialConference, initialDate, initialTeam]);

  const { filters, updateFilter, resetFilters, hasActiveFilters, buildFilterObjects } =
    useRailFilters({
      sport: "ncaaf",
      defaultFilters,
      onFiltersChange
    });

  const formatDateLabel = useCallback((dateStr) => {
    if (!dateStr) return "Select Date";
    if (dateStr instanceof Date) {
      const year = dateStr.getFullYear();
      const month = String(dateStr.getMonth() + 1).padStart(2, "0");
      const day = String(dateStr.getDate()).padStart(2, "0");
      dateStr = `${year}-${month}-${day}`;
    }
    if (typeof dateStr !== "string" || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return "Select Date";
    }
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0);
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    const dateCompare = new Date(date);
    dateCompare.setHours(12, 0, 0, 0);
    if (dateCompare.getTime() === today.getTime()) return "Today";
    if (dateCompare.getTime() === tomorrow.getTime()) return "Tomorrow";
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayName = days[date.getDay()];
    const monthNum = date.getMonth() + 1;
    const dayNum = date.getDate();
    return `${dayName} ${monthNum}/${dayNum}`;
  }, []);

  const formatConferenceLabel = useCallback((conference) => {
    if (!conference) return "All Conferences";
    if (typeof conference === "object") return conference.name || conference.id;
    return conference;
  }, []);

  const formatTeamLabel = useCallback((team) => {
    if (!team) return "All Teams";
    if (typeof team === "object") return team.name || team.id;
    return team;
  }, []);

  const primaryTabs = useMemo(
    () => [
      { id: "all", label: "All Games" },
      { id: "top25", label: "Top 25" },
      { id: "sec", label: "SEC" },
      { id: "big10", label: "Big Ten" },
      { id: "acc", label: "ACC" },
      { id: "big12", label: "Big 12" },
      { id: "pac12", label: "Pac-12" },
      { id: "browse", label: "Browse..."}
    ],
    []
  );

  const filterConfig = useMemo(() => {
    const configs = [
      {
        id: "date",
        type: "date",
        getLabel: formatDateLabel,
        hasDropdown: true
      }
    ];
    if (activeTab === "all") {
      configs.push({
        id: "conference",
        type: "select",
        getLabel: formatConferenceLabel,
        hasDropdown: true
      });
    }
    configs.push({
      id: "team",
      type: "select",
      getLabel: formatTeamLabel,
      hasDropdown: true
    });
    return configs;
  }, [activeTab, formatConferenceLabel, formatDateLabel, formatTeamLabel]);

  const filterObjects = buildFilterObjects(filterConfig);

  const handleFilterClick = useCallback((filterId) => {
    switch (filterId) {
      case "date":
        setShowDateSheet(true);
        break;
      case "conference":
        setShowConferenceSheet(true);
        break;
      case "team":
        setShowTeamSheet(true);
        break;
      default:
        break;
    }
  }, []);

  const handleTabChange = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      if (tabId === "top25") {
        updateFilter("division", "top25");
      } else if (tabId === "all") {
        updateFilter("division", "all");
        updateFilter("conference", null);
      } else if (tabId === "browse") {
        setShowConferenceSheet(true);
      } else {
        updateFilter("division", "all");
        const tabConferenceMap = {
          sec: "SEC",
          big10: "Big Ten",
          acc: "ACC",
          big12: "Big 12",
          pac12: "Pac-12"
        };
        if (tabConferenceMap[tabId]) {
          updateFilter("conference", tabConferenceMap[tabId]);
        }
      }
      onFiltersChange?.({ ...filters, activeTab: tabId });
    },
    [filters, onFiltersChange, updateFilter]
  );

  return {
    filters,
    activeTab,
    hasActiveFilters,
    primaryTabs,
    filterObjects,
    handleTabChange,
    handleFilterClick,
    updateFilter,
    resetFilters,
    showDateSheet,
    setShowDateSheet,
    showConferenceSheet,
    setShowConferenceSheet,
    showTeamSheet,
    setShowTeamSheet
  };
};

export default useNCAAFRailFilters;
